import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LeadNote } from '@spike/client-model';
import moment from 'moment-timezone';
import { FunctionComponent } from 'react';
import styles from './LeadNoteCard.module.scss';

interface LeadNoteCardProps {
    note: LeadNote;
    timeZone: string;
    onEdit: (note: LeadNote) => void;
    onDelete: (note: LeadNote) => void;
}

const initialsOf = (author: string | undefined): string =>
    (author ?? '')
        .split(' ')
        .filter(part => part.length > 0)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');

const timestampOf = (note: LeadNote, timeZone: string): string => {
    const createdAt = moment(note.createdAt).tz(timeZone);
    const day = createdAt.isSame(moment().tz(timeZone), 'day') ? 'Today' : createdAt.format('ddd, D MMMM, YYYY');

    return `on ${day} at ${createdAt.format('h:mma')}`;
};

const LeadNoteCard: FunctionComponent<LeadNoteCardProps> = ({ note, timeZone, onEdit, onDelete }) => (
    <article className={styles.root}>
        {note.staffAvatar ? (
            <img
                className={styles.avatarImage}
                src={note.staffAvatar}
                alt={note.staffName}
            />
        ) : (
            <div className={styles.avatar}>{initialsOf(note.staffName)}</div>
        )}

        <div className={styles.content}>
            <p className={styles.heading}>
                <span className={styles.author}>{note.staffName}</span>
                <span className={styles.timestamp}>{timestampOf(note, timeZone)}</span>
            </p>
            <p className={styles.body}>{note.content}</p>
        </div>

        <div className={styles.actions}>
            <button
                type="button"
                className={styles.action}
                aria-label="Delete note"
                onClick={() => onDelete(note)}
            >
                <FontAwesomeIcon icon={faTrashAlt} />
            </button>
            <button
                type="button"
                className={styles.action}
                aria-label="Edit note"
                onClick={() => onEdit(note)}
            >
                <FontAwesomeIcon icon={faPencil} />
            </button>
        </div>
    </article>
);

export default LeadNoteCard;
